<template>
    <div ref="wrapper" class="relative block transition-all duration-300" :class="{ 'pl-5': normalizedErrors.length }">
        <div
            class="absolute inset-y-0 inset-s-0 w-1.5 origin-left scale-x-0 rounded-full transition"
            :class="[{ 'scale-x-100': normalizedErrors.length }, normalizedErrors.length ? lineColorClass : '']"
        ></div>

        <animate-height :height="normalizedErrors.length ? 'auto' : 0">
            <div
                class="mb-3 flex flex-col gap-1 transition-opacity duration-300"
                :class="{ 'opacity-0': !normalizedErrors.length }"
            >
                <template v-for="error in normalizedErrors" :key="error.message">
                    <slot name="alert" :error="error" :message="error.message">
                        <vx-alert color="danger" :closeable="false">
                            <template v-slot:icon>
                                <font-awesome-icon :icon="faTriangleExclamation" widthAuto></font-awesome-icon>
                            </template>
                            {{ error.message }}
                        </vx-alert>
                    </slot>
                </template>
            </div>
        </animate-height>

        <div>
            <slot></slot>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref, watch, nextTick, computed, unref } from 'vue';
import { VxAlert } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import AnimateHeight from 'vue-animate-height';
import type { ErrorObject } from '@vuelidate/core';

interface NormalizedError {
    message: string;
}

type ErrorsProp = string[] | ErrorObject[] | Array<{ message: string }> | { $errors: ErrorObject[] };

const props = withDefaults(defineProps<{ errors?: ErrorsProp; autoFocus?: boolean; type?: 'danger' | 'warning' }>(), {
    errors: () => [],
    autoFocus: false,
    type: 'danger',
});

const lineColorClass = computed(() => props.type === 'warning' ? 'bg-amber-500' : 'bg-rose-500');

const isMessageObjectArray = (arr: unknown[]): arr is Array<{ message: string }> => {
    return arr.length > 0 && typeof arr[0] === 'object' && arr[0] !== null && 'message' in arr[0];
};

const isStringArray = (arr: unknown[]): arr is string[] => {
    return arr.length > 0 && typeof arr[0] === 'string';
};

const normalizedErrors = computed<NormalizedError[]>(() => {
    if (!props.errors) {
        return [];
    }

    const errorsArray = Array.isArray(props.errors) ? props.errors : props.errors.$errors;

    if (errorsArray.length === 0) {
        return [];
    }

    if (isMessageObjectArray(errorsArray)) {
        return errorsArray.map((e) => ({ message: e.message }));
    }

    if (isStringArray(errorsArray)) {
        return errorsArray.map((msg) => ({ message: msg }));
    }

    return errorsArray
        .map((e) => unref(e.$message))
        .filter((msg): msg is string => typeof msg === 'string')
        .map((msg) => ({ message: msg }));
});

const wrapper = ref<HTMLDivElement | null>(null);

watch(
    () => normalizedErrors.value.length,
    async (newLength: number, oldLength: number) => {
        const focusableElements = wrapper.value?.querySelectorAll<HTMLElement>(
            'input:not([disabled]), textarea:not([disabled]), select:not([disabled])'
        );

        if (newLength > 0) {
            focusableElements?.forEach((el) => {
                el.setAttribute('aria-invalid', 'true');
            });

            if (props.autoFocus && oldLength === 0) {
                await nextTick();
                focusableElements?.[0]?.focus();
            }
        }

        if (newLength === 0) {
            focusableElements?.forEach((el) => {
                el.removeAttribute('aria-invalid');
            });
        }
    }
);
</script>
